/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import React from "react";
// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Custom styles for the SidenavCollapse
import {
  collapseItem,
  collapseIconBox,
  collapseIcon,
  collapseText,
} from "examples/Sidenav/styles/sidenavCollapse";

// Material Dashboard 2 React context
import { useMaterialUIController } from "context";
import { COLORS } from "theme/tokens";

function SidenavCollapse({ icon, name, active, isParent, isOpen, isChild, component, ...rest }) {
  const [controller] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;

  return (
    <ListItemButton
      component={component}
      {...rest}
      sx={(theme) => ({
        ...collapseItem(theme, {
          active,
          transparentSidenav,
          whiteSidenav,
          darkMode,
          sidenavColor,
          isParent,
          isOpen,
          isChild,
          miniSidenav,
        }),
        p: 0,
        "&.active": {
          backgroundColor: COLORS.lighter, // Ensure active NavLink has the peach background
        }
      })}
    >
      <MDBox
        sx={(theme) =>
          collapseItem(theme, {
            active,
            transparentSidenav,
            whiteSidenav,
            darkMode,
            sidenavColor,
            isParent,
            isOpen,
            isChild,
            miniSidenav,
          })
        }
        width="100%"
        display="flex"
        alignItems="center"
      >
        <ListItemIcon
          sx={(theme) => ({
            ...collapseIconBox(theme, { transparentSidenav, whiteSidenav, darkMode, active, isParent, isOpen, isChild }),
            justifyContent: miniSidenav ? "center !important" : "flex-start",
            margin: miniSidenav ? "0 auto" : "0",
          })}
        >
          {typeof icon === "string" ? (
            <Icon sx={(theme) => ({
              ...collapseIcon(theme, { active, isParent, isOpen, transparentSidenav, whiteSidenav }),
              fontSize: "30px !important",
            })}>{icon}</Icon>
          ) : (
            React.isValidElement(icon) ? (
              React.cloneElement(icon, {
                width: "30px",
                height: "30px",
                sx: {
                  ...(icon.props.sx || {}),
                  width: "30px !important",
                  height: "30px !important",
                  display: "block",
                  margin: "0 auto",
                }
              })
            ) : (
              icon
            )
          )}
        </ListItemIcon>

        <ListItemText
          primary={name}
          sx={(theme) =>
            collapseText(theme, {
              miniSidenav,
              transparentSidenav,
              whiteSidenav,
              active,
              isParent,
              isOpen,
              isChild
            })
          }
        />

        {isParent && !miniSidenav && (
          <Icon
            sx={(theme) => ({
              ml: "auto",
              flexShrink: 0,
              minWidth: "24px",
              transition: theme.transitions.create("transform", {
                easing: theme.transitions.easing.easeInOut,
                duration: theme.transitions.duration.shorter,
              }),
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              color: (transparentSidenav && !darkMode) || whiteSidenav ? "dark" : "white",
            })}
          >
            expand_more
          </Icon>
        )}
      </MDBox>
    </ListItemButton>
  );
}

// Setting default values for the props of SidenavCollapse
SidenavCollapse.defaultProps = {
  active: false,
  isParent: false,
  isOpen: false,
  isChild: false,
  component: "div",
};

// Typechecking props for the SidenavCollapse
SidenavCollapse.propTypes = {
  icon: PropTypes.node.isRequired,
  name: PropTypes.string.isRequired,
  active: PropTypes.bool,
  isParent: PropTypes.bool,
  isOpen: PropTypes.bool,
  isChild: PropTypes.bool,
  component: PropTypes.elementType,
};

export default SidenavCollapse;
